import React, { useState, useEffect } from 'react';
import {
    ShieldCheck,
    ShieldAlert,
    Loader2,
    CheckCircle2,
    Mail,
    AlertTriangle,
    RefreshCw,
    Globe,
    ArrowRight,
    Clock,
} from 'lucide-react';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';
import { motion, AnimatePresence } from 'framer-motion';

const SSLStatusCard = () => {
    const [sslStatus, setSslStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const [showTestPreview, setShowTestPreview] = useState(false);
    const [isSendingTest, setIsSendingTest] = useState(false);

    const fetchSSLStatus = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE}/admin/ssl-status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSslStatus(data);
            } else {
                setError('Unable to fetch security status');
            }
        } catch (err) {
            setError('Connection error');
        } finally {
            setLoading(false);
        }
    };

    const handleAcknowledge = async () => {
        setIsAcknowledging(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE}/admin/ssl-status/acknowledge`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                await fetchSSLStatus();
            }
        } catch (err) {
            console.error("Acknowledgement failed:", err);
        } finally {
            setIsAcknowledging(false);
        }
    };

    const handleSendTestEmail = () => {
        setIsSendingTest(true);
        setTimeout(() => {
            setIsSendingTest(false);
            setShowTestPreview(true);
        }, 800);
    };

    useEffect(() => {
        fetchSSLStatus();
    }, []);

    if (loading) {
        return (
            <div className="bg-white rounded-[2rem] p-32 flex flex-col items-center justify-center min-h-[500px] border border-slate-100 shadow-sm">
                <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-6" />
                <p className="text-sm font-medium text-slate-500">Scanning SSL security...</p>
            </div>
        );
    }

    if (error || !sslStatus) {
        return (
            <div className="bg-white rounded-[2rem] p-16 border border-slate-200 shadow-sm text-center">
                <ShieldAlert className="w-16 h-16 text-rose-500 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-slate-900">Security Check Failed</h3>
                <p className="text-slate-500 mt-2">{error || 'Something went wrong.'}</p>
                <button onClick={fetchSSLStatus} className="mt-8 px-10 py-3.5 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-all">
                    Try Again
                </button>
            </div>
        );
    }

    const daysRemaining = sslStatus.days_remaining;
    const isAcknowledged = sslStatus.is_acknowledged;
    const progress = Math.min(100, (daysRemaining / 365) * 100);
    const statusType = daysRemaining <= 3 ? 'critical' : daysRemaining <= 15 ? 'warning' : 'healthy';

    // Precision Slat Calculations
    const issuedDate = sslStatus.issued_at ? new Date(sslStatus.issued_at) : null;
    const expiresDate = sslStatus.expires_at ? new Date(sslStatus.expires_at) : null;
    const today = new Date();
    const daysActive = issuedDate ? Math.floor((today - issuedDate) / (1000 * 60 * 60 * 24)) : 0;

    const theme = {
        healthy: {
            icon: ShieldCheck,
            color: 'text-emerald-600',
            bg: 'bg-emerald-600',
            light: 'bg-emerald-50',
            border: 'border-emerald-100',
            label: 'Certificate is Healthy'
        },
        warning: {
            icon: AlertTriangle,
            color: 'text-amber-600',
            bg: 'bg-amber-600',
            light: 'bg-amber-50',
            border: 'border-amber-100',
            label: 'Attention Required'
        },
        critical: {
            icon: ShieldAlert,
            color: 'text-rose-600',
            bg: 'bg-rose-600',
            light: 'bg-rose-50',
            border: 'border-rose-100',
            label: 'Critical Expiration'
        }
    }[statusType];

    const Icon = theme.icon;

    return (
        <div className="max-w-6xl">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden"
            >
                {/* ─── HEADER: STATUS AT A GLANCE ─── */}
                <div className="p-5">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                        <div className="flex items-center gap-6">
                            <div className={`w-16 h-16 ${theme.light} rounded-2xl flex items-center justify-center border ${theme.border}`}>
                                <Icon className={`w-8 h-8 ${theme.color}`} />
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-1">{theme.label}</h2>
                                <p className="text-slate-500 font-medium flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-slate-400" />
                                    {sslStatus.domain}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={fetchSSLStatus}
                                className="flex items-center gap-2.5 px-6 py-3 bg-slate-900 text-white rounded-2xl text-sm font-bold hover:bg-slate-800 transition-all active:scale-95 group"
                            >
                                <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
                                Check Now
                            </button>
                            <button
                                onClick={handleSendTestEmail}
                                disabled={isSendingTest}
                                className="flex items-center gap-2.5 px-6 py-3 bg-white border-2 border-slate-100 text-slate-900 rounded-2xl text-sm font-bold hover:border-indigo-600 hover:text-indigo-600 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {isSendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                Test Alert
                            </button>
                        </div>
                    </div>
                </div>

                {/* ─── WORLD-CLASS CRYSTAL HUB TIMELINE ─── */}
                <div className="bg-slate-50 p-12 border border-slate-100 flex flex-col lg:flex-row items-center gap-16 relative overflow-hidden">
                    {/* Background Decorative Blur */}
                    <div className={`absolute top-0 right-0 w-64 h-64 ${theme.bg} opacity-[0.03] blur-[100px] -mr-32 -mt-32`} />
                    <div className={`absolute bottom-0 left-0 w-64 h-64 ${theme.bg} opacity-[0.03] blur-[100px] -ml-32 -mb-32`} />

                    {/* Left Side: Crystal Hub */}
                    <div className="relative w-56 h-56 flex-shrink-0 group">
                        {/* Glassmorphic Layer */}
                        <div className="absolute inset-0 bg-white/40 backdrop-blur-xl rounded-full border border-white/60 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)]" />

                        <svg className="absolute inset-0 w-full h-full -rotate-90 transform" viewBox="0 0 160 160">
                            <defs>
                                <linearGradient id="liquidGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="currentColor" className={theme.color} style={{ stopOpacity: 1 }} />
                                    <stop offset="100%" stopColor="currentColor" className={theme.color} style={{ stopOpacity: 0.6 }} />
                                </linearGradient>
                                <filter id="glow">
                                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>

                            {/* Track */}
                            <circle
                                cx="80"
                                cy="80"
                                r="74"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1"
                                className="text-slate-100"
                            />

                            {/* Liquid Progress */}
                            <motion.circle
                                cx="80"
                                cy="80"
                                r="70"
                                fill="none"
                                stroke="url(#liquidGradient)"
                                strokeWidth="12"
                                strokeLinecap="round"
                                filter="url(#glow)"
                                className={theme.color}
                                initial={{ strokeDasharray: "0 440" }}
                                animate={{ strokeDasharray: `${(progress / 100) * 440} 440` }}
                                transition={{ duration: 2, ease: "circOut" }}
                            />

                            {/* Orbital Tracer */}
                            <motion.circle
                                cx="80"
                                cy="80"
                                r="74"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="4"
                                strokeDasharray="2 145"
                                className={theme.color}
                                animate={{ rotate: 360 }}
                                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                            />
                        </svg>

                        {/* Center Typography */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex flex-col items-center"
                            >
                                <span className={`text-7xl font-black tracking-tighter sm:text-7xl ${theme.color} drop-shadow-sm`}>
                                    {daysRemaining}
                                </span>
                                <div className="flex flex-col items-center -mt-2">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Lifecycle Days</span>
                                    <div className={`h-1 w-8 rounded-full ${theme.bg} mt-1 opacity-20`} />
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    {/* Right Side: Precision Info Grid */}
                    <div className="flex-1 w-full space-y-10 z-10">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                            <div className="space-y-2 text-center sm:text-left">
                                <div className="flex items-center justify-center sm:justify-start gap-2">
                                    <div className={`w-2 h-2 rounded-full ${theme.bg} animate-pulse`} />
                                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Digital Asset Protection</span>
                                </div>
                                <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{sslStatus.domain}</h3>
                            </div>
                            <div className={`px-5 py-2 rounded-2xl border-2 ${theme.border} ${theme.light} flex items-center gap-3`}>
                                <ShieldCheck className={`w-5 h-5 ${theme.color}`} />
                                <span className={`text-xs font-black uppercase tracking-widest ${theme.color}`}>{theme.label}</span>
                            </div>
                        </div>

                        {/* Group Slats and Footer for Tighter Spacing */}
                        <div className="space-y-4">
                            {/* Technical Slats: High Precision Readouts (Horizontal) */}
                            <div className="flex flex-col xl:flex-row gap-6">
                                {/* Issuance Slat */}
                                <div className="flex-1 relative group bg-white rounded-2xl p-7 border border-slate-200 overflow-hidden transition-all hover:shadow-lg hover:shadow-emerald-500/5">
                                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-500" />
                                    <div className="flex items-center justify-between relative z-10">
                                        <div className="flex items-center gap-6">
                                            {/* Visual Component: Big Day */}
                                            <div className="flex flex-col items-center min-w-[60px]">
                                                <span className="text-5xl font-black text-slate-900 leading-none">
                                                    {issuedDate ? issuedDate.getDate() : '--'}
                                                </span>
                                                <div className="mt-1 flex flex-col items-center">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                                        {issuedDate ? issuedDate.toLocaleDateString('en-US', { month: 'short' }) : '---'}
                                                    </span>
                                                    <span className="text-[9px] font-bold text-slate-300 tracking-tighter mt-0.5">
                                                        {issuedDate ? issuedDate.getFullYear() : '----'}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Protocol Info */}
                                            <div className="space-y-1">
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Issuance Protocol</span>
                                                <h4 className="text-lg font-bold text-slate-900 tracking-tight leading-tight">Active Certificate Core</h4>
                                            </div>
                                        </div>
                                        {/* Metric Readout */}
                                        <div className="text-right">
                                            <div className="flex items-center justify-end gap-1.5 mb-0.5">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                                <span className="text-xl font-mono font-black text-slate-900">{daysActive}d</span>
                                            </div>
                                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Active Runtime</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Termination Slat */}
                                <div className="flex-1 relative group bg-white rounded-2xl p-7 border border-slate-200 overflow-hidden transition-all hover:shadow-lg hover:shadow-rose-500/5">
                                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${theme.bg}`} />
                                    <div className="flex items-center justify-between relative z-10">
                                        <div className="flex items-center gap-6">
                                            {/* Visual Component: Big Day */}
                                            <div className="flex flex-col items-center min-w-[60px]">
                                                <span className={`text-5xl font-black ${theme.color} leading-none`}>
                                                    {expiresDate ? expiresDate.getDate() : '--'}
                                                </span>
                                                <div className="mt-1 flex flex-col items-center">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                                        {expiresDate ? expiresDate.toLocaleDateString('en-US', { month: 'short' }) : '---'}
                                                    </span>
                                                    <span className="text-[9px] font-bold text-slate-300 tracking-tighter mt-0.5">
                                                        {expiresDate ? expiresDate.getFullYear() : '----'}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Termination Info */}
                                            <div className="space-y-1">
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Termination Schedule</span>
                                                <h4 className="text-lg font-bold text-slate-900 tracking-tight leading-tight">Lifecycle Shutdown Plan</h4>
                                            </div>
                                        </div>
                                        {/* Metric Readout */}
                                        <div className="text-right">
                                            <div className="flex items-center justify-end gap-1.5 mb-0.5">
                                                <Clock className={`w-4 h-4 ${theme.color}`} />
                                                <span className={`text-xl font-mono font-black ${theme.color}`}>{daysRemaining}d</span>
                                            </div>
                                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Remaining TTL</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="flex -space-x-3">
                                        {[1, 2].map(i => (
                                            <div key={i} className="w-10 h-10 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center overflow-hidden shadow-sm">
                                                <div className="w-full h-full bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-600">
                                                    {i === 1 ? 'VS' : 'MS'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-slate-900 leading-tight">Monitoring active for:</p>
                                        <p className="text-[10px] font-medium text-slate-500">vickysrini28@gmail.com, muni@willsparrow.com</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-slate-400">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">Telemetry v2.4</span>
                                    <RefreshCw className="w-3.5 h-3.5" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ─── ACTION SECTION: ACKNOWLEDGEMENT ─── */}
                <div className="bg-indigo-50 p-8 border border-indigo-100 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-5 text-center md:text-left">
                        <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                            <CheckCircle2 className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900">Policy Acknowledgement</h4>
                            <p className="text-sm text-slate-600">Confirm you have reviewed the certificate lifecycle protocol.</p>
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        {isAcknowledged ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="px-8 py-3 bg-emerald-100 text-emerald-700 rounded-xl text-sm font-bold flex items-center gap-2"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                Confirmed
                            </motion.div>
                        ) : (
                            <button
                                onClick={handleAcknowledge}
                                disabled={isAcknowledging}
                                className="px-8 py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:opacity-50"
                            >
                                {isAcknowledging ? 'Confirming...' : 'Acknowledge Policy'}
                            </button>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* ─── TEST PREVIEW MODAL ─── */}
            <AnimatePresence>
                {showTestPreview && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowTestPreview(false)}
                            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white rounded-[2rem] w-full max-w-lg overflow-hidden shadow-2xl relative z-10"
                        >
                            <div className="p-8 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="text-lg font-bold text-slate-900">Email Preview</h2>
                                <button onClick={() => setShowTestPreview(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                                    <ArrowRight className="w-5 h-5 text-slate-400" />
                                </button>
                            </div>
                            <div className="p-10 text-center">
                                <div className={`w-16 h-16 ${theme.light} rounded-3xl flex items-center justify-center mx-auto mb-6`}>
                                    <Icon className={`w-8 h-8 ${theme.color}`} />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-2">SSL Expiration Alert</h3>
                                <p className="text-slate-500 mb-8 px-4">
                                    The SSL certificate for <strong>{sslStatus.domain}</strong> is set to expire in {daysRemaining} days.
                                </p>
                                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 text-left">
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="text-xs font-bold text-slate-400 uppercase">Domain</span>
                                        <span className="text-sm font-bold text-slate-900">{sslStatus.domain}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs font-bold text-slate-400 uppercase">Expires On</span>
                                        <span className={`text-sm font-bold ${theme.color}`}>{new Date(sslStatus.expires_at).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <button className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                                    Manage Certificate
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SSLStatusCard;
